import { Flex, Tag, Text, Tooltip } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { BodyOf, ResponseOf } from "../../core/api";
import { OfficeId } from "../../shared/schema/schema";

// Derived from the endpoint rather than looked up in `Messages`: a Router2 POST
// inlines its request and response types instead of emitting them as named
// schema components, so `Messages["VisitDailyNotePageResponse"]` does not exist.
export type PageBody = BodyOf<"post", "./visit_daily_notes/page">;
export type ListItem = ResponseOf<"post", "./visit_daily_notes/page">["items"][number];
export type PatientGroup = ResponseOf<"post", "./visit_daily_notes/patients/page">["items"][number];

export type Priority = NonNullable<ListItem["priority"]>;
export type Pathway = ListItem["reviewPathways"][number];

/** Where the date filter starts when "Due only" is on: far enough back to reach every due note. */
const DUE_RANGE_START = LocalDate.of(2026, 1, 1);

type DateRange = { fromDate: LocalDate; toDate: LocalDate | null };

/** The window the dashboard opens on: the last month, up to today. */
export function defaultDateRange(): DateRange {
  const today = LocalDate.now();
  return { fromDate: today.minusMonths(1), toDate: today };
}

/**
 * The window "Due only" switches to: from the start of the due range, with no end. When a note
 * is due is the office's setting, 2 days for a concerning note and 14 for another by default,
 * and it is decided on the server, so the window cannot end on a day of its own without hiding
 * a due concerning note. The default last-month window would hide the older ones, though.
 * Unticking the box goes back to the default window.
 */
export function dueDateRange(): DateRange {
  return { fromDate: DUE_RANGE_START, toDate: null };
}

/**
 * Filters the Dashboard tab hands to the patients list when a tile is clicked.
 *
 * Null means the list was opened on its own and starts from its usual last-month window. With a
 * preset there is no default window: a tile's number counts every unsigned note that matches, so
 * the list it opens must too, or the two would disagree.
 */
export type ListPreset = {
  dueOnly: boolean;
  concerning: ConcerningFilter | null;
  officeId: OfficeId | null;
  fromDate: LocalDate | null;
  toDate: LocalDate | null;
};

export function dateRangeForDueOnly(dueOnly: boolean): DateRange {
  return dueOnly ? dueDateRange() : defaultDateRange();
}

export const PATHWAY_OPTIONS = [
  { label: "RN", value: "RN" },
  { label: "Case management", value: "CASE_MANAGEMENT" },
];

/**
 * The "Concerning" filter: the notes the classifier flagged, or the ones it did not. Nothing
 * chosen means both.
 */
export type ConcerningFilter = "CONCERNING" | "NON_CONCERNING";

export const CONCERNING_OPTIONS: { label: string; value: ConcerningFilter }[] = [
  { label: "Concerning", value: "CONCERNING" },
  { label: "Non-concerning", value: "NON_CONCERNING" },
];

/**
 * How a raised critical incident reads, and in what colour.
 *
 * Mirrors the server's derived escalation status: queued, being sent, created in Salesforce,
 * sent back for a person to look at, failed, resolved.
 */
export const ESCALATION_STYLE: Record<
  NonNullable<ListItem["escalationStatus"]>,
  {
    label: string;
    color: string;
  }
> = {
  PENDING_ESCALATION: { label: "Critical incident", color: "red.700" },
  SENDING: { label: "Sending to Salesforce", color: "orange.700" },
  CREATED_IN_SALESFORCE: { label: "Case created", color: "orange.700" },
  NEEDS_REVIEW: { label: "Needs review in Salesforce", color: "red.700" },
  FAILED: { label: "Salesforce send failed", color: "red.700" },
  RESOLVED: { label: "Resolved", color: "green.700" },
};

/**
 * Urgent and high are the two a nurse is scanning for. Routine gets no badge at all
 * rather than a grey one: a badge on every row is a badge on none of them.
 */
export const PRIORITY_STYLE: Record<Priority, { label: string; bg: string; color: string } | null> =
  {
    URGENT: { label: "Urgent", bg: "red.100", color: "red.800" },
    HIGH: { label: "High", bg: "orange.100", color: "orange.800" },
    ROUTINE: null,
  };

/** Marks a note that has waited longer than its office allows for this kind of note. */
export function DueTag() {
  return (
    <Tooltip label="This note has waited longer than the office allows for a note of this kind">
      <Text
        bg="red.50"
        borderColor="red.200"
        borderRadius="sm"
        borderWidth={1}
        color="red.700"
        fontSize="xs"
        fontWeight="semibold"
        px={2}
      >
        Due
      </Text>
    </Tooltip>
  );
}

export function SummaryCell(props: { note: ListItem }) {
  const { note } = props;

  if (note.summary === null) {
    // "No summary" has three causes, and only one of them is "still working". A note the
    // triage step judged routine is finished without the full model ever reading it, so it
    // never has a summary and never has a start time either -- it must not read as pending.
    const label =
      note.analysisState === "FAILED"
        ? "No summary — open the note"
        : note.analysisState === "COMPLETED"
        ? "Non-concerning — no summary needed"
        : "Analyzing…";

    return (
      <Text color="gray.500" fontSize="sm">
        {label}
      </Text>
    );
  }

  const style = note.priority === null ? null : PRIORITY_STYLE[note.priority];

  return (
    // A fixed ceiling on the cell: the summary is a sentence or two, and left to size the
    // column it pushed the rest of the table off screen. Two lines are shown and the whole
    // text is on hover.
    // whiteSpace is reset because table cells are nowrap: without it the clamp has no line
    // to break at and the sentence runs out over the neighbouring columns.
    <Flex
      align="flex-start"
      direction="column"
      gap={1}
      maxW="260px"
      w="260px"
      whiteSpace="normal"
      wordBreak="break-word"
    >
      <Flex gap={1}>
        {style === null ? null : (
          <Tooltip label={note.primaryReason ?? ""}>
            <Text
              bg={style.bg}
              borderRadius="sm"
              color={style.color}
              fontSize="xs"
              fontWeight="semibold"
              px={2}
            >
              {style.label}
            </Text>
          </Tooltip>
        )}
        {/* A routine note routed to case management is still work for someone, and
            without this the row looks like nothing happened. */}
        {note.flagged && style === null ? (
          <Tooltip label={note.primaryReason ?? ""}>
            <Text
              bg="blue.100"
              borderRadius="sm"
              color="blue.800"
              fontSize="xs"
              fontWeight="semibold"
              px={2}
            >
              Concerning
            </Text>
          </Tooltip>
        ) : null}
      </Flex>
      <Tooltip hasArrow={true} label={note.summary} maxW="480px" openDelay={300}>
        <Text fontSize="sm" noOfLines={2}>
          {note.summary}
        </Text>
      </Tooltip>
    </Flex>
  );
}

/**
 * How many rows the current filters leave, as a count next to a heading.
 *
 * The table pages its results, so without this there is no way to tell how many notes,
 * patients or attestations a filter matched. Hidden until the first page has loaded, so it
 * never reads "0" while the real answer is still on its way.
 */
export function ResultCount(props: { count: number; isLoading: boolean; noun: string }) {
  if (props.isLoading) {
    return null;
  }

  return (
    <Tag colorScheme="gray" size="md" variant="subtle">
      {props.count} {props.count === 1 ? props.noun : `${props.noun}s`}
    </Tag>
  );
}
